import dayjs from 'dayjs';
import gqlClient from '@discover-data/gql/client';
import { DATE_FORMAT_DAY_JS } from '@discover-lib/dateFormats';
import isNonNull from '@discover-lib/isNonNull';
import FilteredRestaurantsCarouselQuery, {
  type QueryVariables,
} from '@discover-data/gql/queries/filteredRestaurantsCarousel';

export type FilteredRestaurantsCarouselMode = 'newArrivals' | 'mostPopular' | 'availableToday';

const MIN_NEW_ARRIVALS = 3;

function getQueryVariables(
  mode: FilteredRestaurantsCarouselMode,
  availableTodayDate?: string
): Omit<QueryVariables, 'regionId'> {
  switch (mode) {
    case 'mostPopular':
      return { sort: 'RANKING' };
    case 'availableToday':
      return {
        sort: 'FEATURED_NEW_TOP',
        sessions: ['DINNERANY'],
        dates: [availableTodayDate ?? dayjs().format(DATE_FORMAT_DAY_JS)],
      };
    case 'newArrivals':
      return { sort: 'NEWEST', features: ['IS_NEW'] };
    default: {
      const unreachable: never = mode;
      return unreachable;
    }
  }
}

export type GetFilteredRestaurantsCarouselParams = {
  regionId: number;
  mode: FilteredRestaurantsCarouselMode;
  abortSignal?: AbortSignal;
  /** City-local today for `availableToday`. Omitted callers keep the browser-local date. */
  availableTodayDate?: string;
};

async function fetchCarouselRestaurants(variables: QueryVariables, abortSignal?: AbortSignal) {
  const { allRestaurants } = await gqlClient({
    query: FilteredRestaurantsCarouselQuery,
    variables,
    abortSignal,
  });

  return (allRestaurants || []).filter((restaurant) => isNonNull(restaurant, ['id']));
}

type CarouselRestaurant = Awaited<ReturnType<typeof fetchCarouselRestaurants>>[number];

async function withNewArrivalsFiller(
  eligible: CarouselRestaurant[],
  regionId: number,
  abortSignal?: AbortSignal
) {
  if (eligible.length >= MIN_NEW_ARRIVALS) {
    return eligible;
  }

  const newest = await fetchCarouselRestaurants({ regionId, sort: 'NEWEST' }, abortSignal);

  // IS_NEW rows are always `isNew`, and this list includes them, so drop those ids to avoid duplicates.
  const eligibleIds = new Set(eligible.map(({ id }) => id));
  // Opted-out restaurants stay `isNew` and miss the IS_NEW query, and they are valid backfill.
  const filler = newest.filter(({ id }) => !eligibleIds.has(id));

  return [...eligible, ...filler.slice(0, MIN_NEW_ARRIVALS - eligible.length)];
}

export default async function getFilteredRestaurantsCarousel({
  regionId,
  mode,
  abortSignal,
  availableTodayDate,
}: GetFilteredRestaurantsCarouselParams) {
  const restaurants = await fetchCarouselRestaurants(
    {
      regionId,
      ...getQueryVariables(mode, availableTodayDate),
    },
    abortSignal
  );

  return mode === 'newArrivals'
    ? withNewArrivalsFiller(restaurants, regionId, abortSignal)
    : restaurants;
}

export type FilteredRestaurant = Awaited<ReturnType<typeof getFilteredRestaurantsCarousel>>[number];
